import { Asset, director, gfx, sys, macro, warn } from 'cc';
import AssetCache from './Toolkit/Helper/Loading/AssetCache';
import LoadingTask from './Toolkit/Helper/Loading/LoadingTask/LoadingTask';
import CompositeLoadingTask from './Toolkit/Helper/Loading/LoadingTask/CompositeLoadingTask';
import { TaskNamingHelper } from './BundleAssetProvider';
import { joinURL } from './Toolkit/Utils/Utility';
import RemoteLoader from './Toolkit/Helper/Loading/RemoteLoader';
import RemoteAssetLoadingConfigType, {
    AssetName,
    assetNameToAssetTypeMap,
    AssetNameTypeMap,
    FontPathLoadingConfigType,
    ImageFormat,
    PathLoadingConfigType,
} from './Config/RemoteAssetLoadingConfigType';

function checkImageFormatSupport(imagesFormats: ImageFormat[]): ImageFormat {
    const supportTextureFormats = macro.SUPPORT_TEXTURE_FORMATS;
    let ext: ImageFormat = null;
    let preferredExtensionIndex = Number.MAX_VALUE;

    for (let i = 0; i < imagesFormats.length; i++) {
        const tmpExt = imagesFormats[i];
        const index = supportTextureFormats.indexOf(tmpExt);

        if (index !== -1 && index < preferredExtensionIndex) {
            // Check ASTC support
            if (tmpExt === ImageFormat.ASTC) {
                if (macro.ENABLE_ASTC_ONLY_IOS && sys.os !== sys.OS.IOS) {
                    continue;
                }
                if (!director.root?.device || !(director.root.device.getFormatFeatures(gfx.Format.ASTC_RGBA_4X4) & gfx.FormatFeatureBit.SAMPLED_TEXTURE)) {
                    continue;
                }
            }
            // Check AVIF support
            else if (tmpExt === ImageFormat.AVIF && !sys.hasFeature(sys.Feature.AVIF)) {
                continue;
            }
            // Check WebP support
            else if (tmpExt === ImageFormat.WEBP && !sys.hasFeature(sys.Feature.WEBP)) {
                continue;
            }

            preferredExtensionIndex = index;
            ext = tmpExt;
        }
    }

    if (!ext) {
        warn(`None of the provided formats: ${imagesFormats.join(', ')} is supported`);
        return null;
    }
    return ext;
}

type AssetTypePathValue = AssetNameTypeMap[keyof AssetNameTypeMap];

class RemoteAssetLoadingConfig {
    public readonly name: string;
    public readonly baseURL: string;
    public readonly prefix: string;
    public readonly prefixWithAssetName: boolean;
    public readonly path: Partial<{
        [K in keyof AssetNameTypeMap]: Record<string, AssetNameTypeMap[K]>;
    }>;
    private readonly imageExt: ImageFormat;

    constructor(
        readonly config: RemoteAssetLoadingConfigType,
        private readonly _cache: AssetCache,
        private readonly _getProviderName: () => string,
    ) {
        this.name = config.baseURL + (config.prefix || '');
        this.baseURL = config.baseURL;
        this.prefix = config.prefix || '';
        this.prefixWithAssetName = config.prefixWithAssetName ?? false;

        this.imageExt = checkImageFormatSupport(config.imagesFormats);

        this.path = Object.entries(config.path).reduce((pathObj, [assetName, pathConfig]) => {
            pathObj[assetName] = Object.entries(pathConfig).reduce((pathConfigMap, [key, path]) => {
                if (!path) {
                    delete pathConfigMap[key];
                } else {
                    pathConfigMap[key] = this._createFullPathConfig(assetName as AssetName, path);
                }
                return pathConfigMap;
            }, {});
            return pathObj;
        }, {});
    }

    public load(): LoadingTask {
        const taskName = TaskNamingHelper.concatTaskName(this._getProviderName(), { configName: this.name });
        const task = new CompositeLoadingTask(taskName);

        // load assets
        Object.entries(this.path).forEach(([assetName, assetPathMap]) => {
            const assetType = assetNameToAssetTypeMap[assetName as AssetName];
            Object.entries(assetPathMap).forEach(([assetKey, pathConfig]) => {
                const loadAssetTaskName = TaskNamingHelper.concatTaskName(task.name, { assetName: `${assetName} : ${assetKey}` });
                task.addSimpleTask(loadAssetTaskName, this._loadAsset(assetType, assetKey, pathConfig));
            });
        });

        if (!task.subTaskCount) {
            return LoadingTask.Success;
        }

        return task;
    }

    private async _loadAsset<T extends typeof Asset>(assetType: T, assetKey: string, pathConfig: AssetTypePathValue): Promise<InstanceType<T>> {
        let asset = this._cache.get(assetType, assetKey);
        if (!asset) {
            const args: string[] = [];
            if (pathConfig instanceof Object && 'fontPath' in pathConfig) {
                args.push(pathConfig.fontPath, pathConfig.imagePath);
            } else if (pathConfig instanceof Object && 'path' in pathConfig) {
                args.push(pathConfig.path);
            } else if (typeof pathConfig === 'string') {
                args.push(pathConfig);
            } else {
                throw new Error(`Invalid path config for asset: ${assetKey} of type: ${assetType.name} in provider: ${this._getProviderName()}`);
            }
            asset = await RemoteLoader.load(assetType, args);
            if (asset) {
                this._cache.set(assetType, assetKey, asset);
            }
        }
        return asset;
    }

    private _createFullPathConfig(assetName: AssetName, pathConfig: AssetTypePathValue): AssetTypePathValue {
        // Font asset
        if (assetName === AssetName.FONT) {
            const config = pathConfig as FontPathLoadingConfigType;
            const fontPath = this._setExtIfNeeded(config.fontPath, '.fnt');
            config.fontPath = this._getFullPath(fontPath, assetName);
            if (!config.imagePath) {
                config.imagePath = config.fontPath.replace(/\.fnt$/, this.imageExt);
            } else {
                let { imagePath } = config;
                const ext = this._extname(config.imagePath);
                if (!ext) {
                    imagePath = this._setExtIfNeeded(imagePath, this.imageExt);
                }
                config.imagePath = imagePath;
            }
        }
        // SpriteFrame asset
        else if (assetName === AssetName.SPRITE_FRAME) {
            const config = pathConfig as PathLoadingConfigType;
            let path = typeof config === 'string' ? config : config.path;
            const ext = this._extname(path);
            if (!ext) {
                // Auto set image extension if not provided
                path = this._setExtIfNeeded(path, this.imageExt);
            }
            path = this._getFullPath(path, assetName);
            pathConfig = { path };
        } else if (assetName === AssetName.JSON && typeof pathConfig === 'string') {
            const config = pathConfig;
            pathConfig = this._getFullPath(this._setExtIfNeeded(config, '.json'), assetName);
        } else if (typeof pathConfig === 'string') {
            pathConfig = this._getFullPath(pathConfig, assetName);
        }
        return pathConfig;
    }

    private _getFullPath(path: string, assetName: AssetName): string {
        if (this.prefixWithAssetName) {
            return joinURL(this.baseURL, ...[this.prefix, assetName, path].filter(Boolean));
        }
        return joinURL(this.baseURL, ...[this.prefix, path].filter(Boolean));
    }

    private _setExtIfNeeded(path: string, ext: string): string {
        const hasExt = this._extname(path);
        if (!hasExt) {
            return `${path}${ext}`;
        }
        return path;
    }

    private _extname(path: string) {
        const EXTNAME_RE = /(\.[^./?\\]*)(\?.*)?$/;
        const match = EXTNAME_RE.exec(path);
        return match ? match[1] : '';
    }
}

class Utility {
    constructor(private _provider: RemoteAssetProvider) {}

    /**
     * 過濾載入過的資源設置
     */
    public getUnloadedAssetConfigs(configs: RemoteAssetLoadingConfigType | RemoteAssetLoadingConfigType[]): RemoteAssetLoadingConfigType[] {
        configs = Array.isArray(configs) ? configs : [configs];
        configs = JSON.parse(JSON.stringify(configs)) as RemoteAssetLoadingConfigType[]; // deep clone to avoid mutating original config
        configs.forEach((config) => {
            Object.entries(config.path).forEach(([assetName, assetPathMap]) => {
                const assetType = assetNameToAssetTypeMap[assetName as AssetName];
                Object.entries(assetPathMap).forEach(([key]) => {
                    if (this._provider.get(key, assetType)) {
                        delete assetPathMap[key];
                    }
                });
                if (Object.keys(assetPathMap).length === 0) {
                    delete config.path[assetName as AssetName];
                }
            });
        });
        return configs.filter((config) => Object.keys(config.path).length > 0);
    }
}

export default class RemoteAssetProvider {
    public static readonly DEFAULT_IMAGE_FORMAT: ImageFormat[] = [ImageFormat.ASTC, ImageFormat.AVIF, ImageFormat.WEBP, ImageFormat.PNG];
    public readonly utility = new Utility(this);
    public readonly name: string = '';
    private readonly _cache: AssetCache = new AssetCache();
    private readonly _imageFormat: ImageFormat[] = [];
    private _currentLoadTask: CompositeLoadingTask = null;

    constructor(name: string, imageFormat?: ImageFormat[]) {
        this.name = name || '';
        this._imageFormat = imageFormat || RemoteAssetProvider.DEFAULT_IMAGE_FORMAT;
    }

    public preload(configs: RemoteAssetLoadingConfigType | RemoteAssetLoadingConfigType[]): LoadingTask {
        if (this._currentLoadTask) {
            if (this._currentLoadTask.isFinished) {
                this._currentLoadTask = null;
            } else {
                throw new Error(`RemoteAssetProvider ${this.name} is already loading remote assets.`);
            }
        }
        return this._load(configs, true);
    }

    public load(configs: RemoteAssetLoadingConfigType | RemoteAssetLoadingConfigType[]) {
        return this._load(configs, false);
    }

    public get<T extends typeof Asset>(assetName: string, assetType: T): InstanceType<T> {
        return this._cache.get(assetType, assetName);
    }

    public getAll<T extends typeof Asset>(assetType: T): Record<string, InstanceType<T>> {
        return this._cache.getAll(assetType);
    }

    public async waitForLoaded(): Promise<void> {
        if (!this._currentLoadTask) {
            return;
        }
        await this._currentLoadTask.waitForFinish();
        if (!this._currentLoadTask.isSuccess) {
            throw new Error(`Failed to load remote assets for provider: ${this.name}`);
        }
    }

    private _createAssetLoadingConfigs(assetLoadingConfig: RemoteAssetLoadingConfigType | RemoteAssetLoadingConfigType[]): RemoteAssetLoadingConfig[] {
        return (Array.isArray(assetLoadingConfig) ? assetLoadingConfig : [assetLoadingConfig]).map((config) => {
            if (!config.imagesFormats) {
                config.imagesFormats = this._imageFormat;
            }
            return new RemoteAssetLoadingConfig(config, this._cache, () => this.name);
        });
    }

    private _load(configs: RemoteAssetLoadingConfigType | RemoteAssetLoadingConfigType[], isPreload: boolean) {
        const taskName = TaskNamingHelper.concatTaskName({
            providerName: this.name,
        });

        const loadingTask = new CompositeLoadingTask(taskName);
        if (isPreload) {
            this._currentLoadTask = loadingTask;
        }
        this._createAssetLoadingConfigs(configs).forEach((assetLoadingConfig) => loadingTask.addTask(assetLoadingConfig.load()));
        return loadingTask;
    }
}
